import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, Save, Users, Wrench, ShieldAlert, Power, CheckCircle2 } from 'lucide-react';
import { discoverService } from '../../services/DiscoverService';

const SettingsPage: React.FC<{ onProfileUpdate?: () => void }> = ({ onProfileUpdate }) => {
    // Shared State

    // Maintenance Mode State
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [loadingMaintenance, setLoadingMaintenance] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Profile State
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [fullName, setFullName] = useState('');
    const [role, setRole] = useState('admin');
    const [email, setEmail] = useState('');

    useEffect(() => {
        loadUserProfile();
        loadMaintenanceStatus();
    }, []);

    const loadMaintenanceStatus = async () => {
        try {
            const settings = await discoverService.getSettings();
            if (settings) {
                setIsMaintenanceMode(!!settings.is_maintenance_mode);
            }
        } catch (error) {
            console.error('Error loading maintenance status:', error);
        }
    };

    const handleToggleMaintenance = async () => {
        try {
            setLoadingMaintenance(true);
            setMaintenanceMessage(null);
            const nextState = !isMaintenanceMode;
            const success = await discoverService.updateSettings({
                is_maintenance_mode: nextState
            });
            if (success) {
                setIsMaintenanceMode(nextState);
                setMaintenanceMessage({
                    type: 'success',
                    text: nextState
                        ? 'Modo Mantenimiento ACTIVADO. Los visitantes externos verán la pantalla de mantenimiento.'
                        : 'Modo Mantenimiento DESACTIVADO. El catálogo público está disponible normalmente.'
                });
            } else {
                setMaintenanceMessage({
                    type: 'error',
                    text: 'No se pudo actualizar el estado de mantenimiento en el servidor.'
                });
            }
        } catch (error) {
            console.error('Error toggling maintenance mode:', error);
            setMaintenanceMessage({
                type: 'error',
                text: 'Error de conexión al actualizar el modo mantenimiento.'
            });
        } finally {
            setLoadingMaintenance(false);
        }
    };

    // --- PROFILE LOGIC ---
    const loadUserProfile = async () => {
        try {
            setLoadingProfile(true);
            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
                setEmail(user.email || '');

                const { data: profile } = await supabase
                    .from('profiles')
                    .select('full_name, role')
                    .eq('id', user.id)
                    .single();

                if (profile) {
                    setFullName(profile.full_name || '');
                    setRole(profile.role || 'admin');
                }
            }
        } catch (error) {
            console.error('Error loading profile:', error);
        } finally {
            setLoadingProfile(false);
        }
    };

    const sanitizeInput = (input: string) => {
        return input.replace(/[^a-zA-Z0-9\sÁÉÍÓÚáéíóúÑñ|]/g, '').trim();
    };

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoadingProfile(true);
        setProfileMessage(null);

        const sanitizedFullName = sanitizeInput(fullName);

        if (sanitizedFullName.length < 3) {
            setProfileMessage({ type: 'error', text: 'El nombre debe tener al menos 3 caracteres válidos.' });
            setLoadingProfile(false);
            return;
        }

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('No user logged in');

            const updates = {
                id: user.id,
                full_name: sanitizedFullName,
                updated_at: new Date().toISOString(),
            };

            const { error } = await supabase
                .from('profiles')
                .upsert(updates);

            if (error) throw error;

            setProfileMessage({ type: 'success', text: 'Perfil actualizado correctamente' });
            setFullName(sanitizedFullName);

            if (onProfileUpdate) {
                onProfileUpdate();
            }

        } catch (error) {
            setProfileMessage({ type: 'error', text: 'Error al actualizar el perfil' });
            console.error(error);
        } finally {
            setLoadingProfile(false);
        }
    };

    // --- BRANCHES LOGIC (REMOVED) ---

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-display font-bold uppercase italic text-white mb-6">
                Configuración del <span className="text-white">Sistema</span>
            </h2>


            {/* CONTENT: SYSTEM MAINTENANCE */}
            <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8 rounded-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-zinc-800 gap-4">
                    <div>
                        <h3 className="text-lg font-bold uppercase tracking-widest text-white flex items-center gap-2">
                            <Wrench size={20} className="text-white" /> Estado del Sistema & Catálogo
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1">
                            Controla la disponibilidad pública de la tienda web para visitantes.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold uppercase ${
                            isMaintenanceMode 
                                ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400' 
                                : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                        }`}>
                            <span className={`w-2 h-2 rounded-full ${isMaintenanceMode ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
                            {isMaintenanceMode ? 'Mantenimiento Activo' : 'Sitio Público Activo'}
                        </span>
                    </div>
                </div>

                <div className="py-6 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-black border border-zinc-800 rounded-sm">
                        <div className="space-y-1">
                            <p className="text-sm font-bold text-white uppercase tracking-wider">
                                Bloqueo de Sitio por Mantenimiento
                            </p>
                            <p className="text-xs text-zinc-400 leading-relaxed max-w-lg">
                                Al activar este switch, los visitantes públicos verán la pantalla de mantenimiento con botón de WhatsApp. Los administradores con sesión activa conservan acceso completo a la web y al panel.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={handleToggleMaintenance}
                            disabled={loadingMaintenance}
                            className={`px-6 py-3 rounded-sm font-display font-black uppercase text-xs tracking-widest transition-all flex items-center gap-2 justify-center flex-shrink-0 ${
                                isMaintenanceMode
                                    ? 'bg-amber-500 hover:bg-amber-400 text-black shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                                    : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700'
                            }`}
                        >
                            {loadingMaintenance ? (
                                <Loader2 className="animate-spin w-4 h-4" />
                            ) : (
                                <Power className="w-4 h-4" />
                            )}
                            <span>{isMaintenanceMode ? 'Desactivar Modo Mantenimiento' : 'Activar Modo Mantenimiento'}</span>
                        </button>
                    </div>

                    {maintenanceMessage && (
                        <div className={`p-4 text-xs font-bold text-center border rounded-sm ${
                            maintenanceMessage.type === 'success'
                                ? 'bg-green-900/20 border-green-900 text-green-400'
                                : 'bg-red-900/20 border-red-900 text-red-400'
                        }`}>
                            {maintenanceMessage.text}
                        </div>
                    )}
                </div>
            </div>

            {/* CONTENT: PROFILE */}
            <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8 rounded-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h3 className="text-lg font-bold uppercase tracking-widest text-white mb-6 border-b border-zinc-800 pb-4 flex items-center gap-2">
                    <Users size={20} className="text-white" /> Perfil de Usuario
                </h3>

                <form onSubmit={handleSaveProfile} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                                Correo Electrónico
                            </label>
                            <input
                                type="email"
                                value={email}
                                disabled
                                className="w-full bg-black border border-zinc-800 text-zinc-400 px-4 py-3 rounded-sm text-sm cursor-not-allowed"
                            />
                            <p className="text-[10px] text-zinc-600 italic">El correo no se puede cambiar.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-mono text-white uppercase tracking-widest">
                                Nombre para Mostrar
                            </label>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="w-full bg-black border border-zinc-800 text-white px-4 py-3 rounded-sm text-sm focus:border-white focus:ring-1 focus:ring-white outline-none transition-all"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                                Rol del Sistema
                            </label>
                            <div className="w-full bg-black border border-zinc-800 text-zinc-400 px-4 py-3 rounded-sm text-sm flex items-center justify-between">
                                <span className="uppercase font-bold">{role}</span>
                                <span className="text-[10px] bg-zinc-900 px-2 py-1 rounded border border-zinc-800 text-zinc-500">Nivel de Acceso</span>
                            </div>
                        </div>
                    </div>

                    {profileMessage && (
                        <div className={`p-4 text-sm font-bold text-center border ${profileMessage.type === 'success'
                            ? 'bg-green-900/20 border-green-900 text-green-500'
                            : 'bg-red-900/20 border-red-900 text-red-500'
                            }`}>
                            {profileMessage.text}
                        </div>
                    )}

                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={loadingProfile}
                            className="bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-sm px-8 py-3 transition-all tracking-widest hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loadingProfile ? <Loader2 className="animate-spin h-4 w-4" /> : <Save className="h-4 w-4" />}
                            {loadingProfile ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </form>
            </div>

        </div>
    );
};

export default SettingsPage;
